// Home "Graphite + Lime" (2026-10-27, PREVIEW ONLY — rendered by src/preview/HomeGraphitePreview.jsx; not wired into
// the app). Presentation only: every value comes in as props (the data the existing APIs return), nothing is fetched,
// no AI is called. A metric the source did not supply is not rendered (no "--", no 0, no empty tile).
import React from 'react';
import './homeGraphite.css';
import { weekDays, weekRangeLabel, isoOf, planDaysByDate, hasRunningPlan, runsOn } from './todayModel';
import { garminHomeState } from '../home2/TrainingSourcesCard';
import { deepAnalysisButton } from '../home2/HomeA2';
import {
  shortDate, dotKind, zoneLabel, lastRun, whenLabel, visibleMetrics, paceView, todayView, nextView, weekView,
  lastWeekView, daysLabel, planPaused, injuryCantRun, PLAN_PAUSED,
} from './homeGraphiteModel';

// Outline icons, 22px, stroke 1.7 (the spec) — no icon font, no emoji.
const ICON = {
  home: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
  plan: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
  coach: <path d="M4 5h16v11H9l-5 4z" />,
  activities: <path d="M3 12h4l3-7 4 14 3-7h4" />,
  profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></>,
};
function Icon({ name }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">{ICON[name]}</svg>
  );
}

const TABS = [
  { key: 'home', label: 'בית', icon: 'home' },
  { key: 'weekly-plan', label: 'תכנית', icon: 'plan' },
  { key: 'chat', label: 'מאמן', icon: 'coach' },
  { key: 'activities', label: 'אימונים', icon: 'activities' },
  { key: 'profile', label: 'פרופיל', icon: 'profile' },
];

/** The shared bottom bar — 5 tabs, RTL (בית on the right). The coach dot only for a real unread message. */
export function GraphiteBottomNav({ active = 'home', onNavigate, unreadCoach = false }) {
  return (
    <nav className="hg-nav" data-theme="graphite" dir="rtl" aria-label="ניווט ראשי" data-testid="hg-nav">
      <div className="hg-nav-in">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={`hg-tab${t.key === active ? ' is-on' : ''}`}
                  aria-current={t.key === active ? 'page' : undefined} data-testid={`hg-tab-${t.key}`}
                  onClick={() => onNavigate && onNavigate(t.key)}>
            <Icon name={t.icon} />
            {t.key === 'chat' && unreadCoach && <span className="hg-badge" aria-label="הודעה חדשה מהמאמן" />}
            {t.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function Ring({ ring }) {
  const R = 45;
  const C = 2 * Math.PI * R;
  return (
    <div className="hg-ring" role="img" aria-label={`השפעה אירובית ${ring.value} מתוך 5`} data-testid="hg-ring">
      <svg viewBox="0 0 96 96" aria-hidden="true">
        <circle cx="48" cy="48" r={R} fill="none" stroke="var(--g-line)" strokeWidth="6" />
        <circle cx="48" cy="48" r={R} fill="none" stroke="var(--g-lime)" strokeWidth="6"
                strokeDasharray={`${(C * Math.min(1, ring.frac)).toFixed(1)} ${C.toFixed(1)}`} />
      </svg>
      <div className="hg-ring-c" aria-hidden="true">
        <span className="hg-num hg-ring-v" dir="ltr">{ring.value}</span>
        <span className="hg-ring-l">{ring.label}</span>
      </div>
    </div>
  );
}

// "ניתוח מעמיק בצ׳אט" (2026-11-02): the existing deep-analysis action of the previous Home (App.js startDeepAnalysis →
// POST /chat/analyze-workout → the chat). Click only — nothing runs on load. States from the backend's per-workout
// state (home2/HomeA2.deepAnalysisButton); only the idle label is the Graphite one.
const DEEP_IDLE_LABEL = 'ניתוח מעמיק בצ׳אט';
function DeepAnalysisAction({ btn }) {
  const toChat = btn.state === 'none' || btn.state === 'failed';
  const label = btn.state === 'none' ? DEEP_IDLE_LABEL : btn.label;
  return (
    <button type="button" className="hg-btn hg-deep" data-testid="deep-analysis-button" data-state={btn.state}
            disabled={btn.disabled} aria-busy={btn.state === 'analyzing' ? 'true' : undefined}
            onClick={() => { if (!btn.disabled && btn.onClick) btn.onClick(); }}>
      {toChat && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
             strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /></svg>
      )}
      <span>{label}</span>
      {toChat && <span aria-hidden="true">←</span>}
    </button>
  );
}

// "התוכנית שלי" while an injury pauses the plan (cant_run, no return started): today rest, the next workout paused —
// no run offered — and "עדכון מצב פציעה": navigation only, to the profile's injury card (nothing is written here).
function PlanPaused({ onUpdateInjury }) {
  const P = PLAN_PAUSED;
  return (
    <div className="hg-card" data-testid="hg-plan-paused">
      <div className="hg-plan-head">
        <span className="hg-coach-l">{P.title}</span>
        <span className="hg-chip" data-testid="hg-plan-chip">{P.chip}</span>
      </div>
      <div className="hg-pair">
        {[['today', P.today], ['next', P.next]].map(([k, v]) => (
          <div key={k} className="hg-small hg-inner" data-testid={`hg-${k}`} data-kind="paused">
            <div className="hg-l">{v.label}</div>
            <div className="hg-t">{v.title}</div>
            <div className="hg-s">{v.sub}</div>
          </div>
        ))}
      </div>
      {onUpdateInjury && (
        <>
          <p className="hg-guide" data-testid="hg-injury-guide">{P.guide}</p>
          <button type="button" className="hg-btn hg-deep hg-return" data-testid="hg-update-injury"
                  onClick={() => onUpdateInjury()}>
            {P.action}
          </button>
        </>
      )}
    </div>
  );
}

// "לא רץ" with a return date (the plan cards show the return weeks): the same action as a compact row next to them —
// the guidance + "עדכון מצב פציעה", navigation only (nothing is written here).
function InjuryUpdateRow({ onUpdateInjury }) {
  return (
    <div className="hg-alert hg-injury-row" data-testid="hg-injury-row">
      <p className="hg-guide" data-testid="hg-injury-guide">{PLAN_PAUSED.guide}</p>
      <button type="button" className="hg-btn hg-deep" data-testid="hg-update-injury" onClick={() => onUpdateInjury()}>
        {PLAN_PAUSED.action}
      </button>
    </div>
  );
}

function LastWorkout({ act, planDay, splits, coachText, onOpen, onCoach, deep }) {
  const m = visibleMetrics(act);
  const pace = paceView(splits);
  const zone = zoneLabel(planDay);
  const title = (planDay && !planDay.is_rest && planDay.type) || 'ריצה';
  return (
    <div className="hg-card" data-testid="hg-last">
      <button type="button" className="hg-btn" style={{ width: '100%' }} onClick={onOpen}
              aria-label={`האימון האחרון: ${title}, ${m.distance} ק״מ`}>
        <div className="hg-lw-top">
          <span>האימון האחרון</span>
          {whenLabel(act) && <span dir="rtl" data-testid="hg-when">{whenLabel(act)}</span>}
        </div>
        <div className="hg-lw-title">{title}{zone && <span className="hg-sub"> · {zone}</span>}</div>
        <div className="hg-hero">
          <div className="hg-dist">
            <span className="hg-num hg-big" dir="ltr" data-testid="hg-distance">{m.distance}</span>
            <span className="hg-unit">ק״מ</span>
          </div>
          {m.ring && <Ring ring={m.ring} />}
        </div>
        {m.primary.length > 0 && (
          <>
            <hr className="hg-line" />
            <div className="hg-metrics" data-testid="hg-primary"
                 style={{ gridTemplateColumns: `repeat(${m.primary.length}, minmax(0, 1fr))` }}>
              {m.primary.map((x) => (
                <div key={x.key} className="hg-metric" data-testid={`hg-m-${x.key}`}>
                  <div className="hg-num hg-v" dir="ltr">{x.value}</div>
                  <div className="hg-l">{x.label}</div>
                </div>
              ))}
            </div>
          </>
        )}
        {m.secondary.length > 0 && (
          <div className="hg-secondary" data-testid="hg-secondary">
            {m.secondary.map((x) => (
              <span key={x.key} data-testid={`hg-s-${x.key}`}>
                {x.label} <b dir="ltr">{x.value}</b>{x.unit ? ` ${x.unit}` : ''}
              </span>
            ))}
          </div>
        )}
        {pace && (
          <div role="img" aria-label={`קצב לפי קילומטר: ${pace.bars.map((b) => `ק״מ ${b.key} ${b.pace}`).join(', ')}`}
               data-testid="hg-pace">
            <div className="hg-pace-head"><span>קצב לפי ק״מ</span>{pace.fastest && <span>{pace.fastest}</span>}</div>
            <div className="hg-pace" aria-hidden="true">
              {pace.bars.map((b) => (
                <span key={b.key} className={b.fastest ? 'is-fast' : ''} style={{ height: `${Math.round(b.rel * 100)}%` }} />
              ))}
            </div>
          </div>
        )}
      </button>
      {coachText && (
        <>
          <hr className="hg-line" />
          <button type="button" className="hg-btn" style={{ width: '100%' }} onClick={onCoach} data-testid="hg-coach">
            <div className="hg-coach-l">המאמן</div>
            <p className="hg-coach-t">{coachText}</p>
          </button>
          {deep && <DeepAnalysisAction btn={deep} />}
        </>
      )}
    </div>
  );
}

function WeekCard({ view, range, lastWeek }) {
  return (
    <div className="hg-card" data-testid="hg-week">
      <div className="hg-muted" style={{ fontSize: 13 }}>השבוע · <span dir="ltr">{range}</span></div>
      <div className="hg-wk-top">
        <div className="hg-wk-head">
          <span className="hg-num hg-big" dir="ltr" data-testid="hg-week-km">{view.plannedKm}</span>
          <span className="hg-unit">ק״מ מתוכננים</span>
        </div>
        {view.progress && (
          <div className="hg-wk-prog" data-testid="hg-week-progress"
               aria-label={`ב־${view.progress.done} מתוך ${view.progress.of} ימי האימון נרשמה פעילות`}>
            <b dir="ltr">{`${view.progress.done} / ${view.progress.of}`}</b> ימים עם פעילות
          </div>
        )}
      </div>
      <div className="hg-bars" role="img" data-testid="hg-week-bars"
           aria-label={`קילומטרים מתוכננים לפי יום: ${view.bars.map((b) => `${b.letter} ${b.planned || 0}`).join(', ')}`}>
        {view.bars.map((b) => (
          <div key={b.iso} className="hg-col" aria-hidden="true">
            {b.planned > 0 ? (
              <>
                <div className="hg-km hg-num">{b.planned}</div>
                <div className={`hg-box${b.quality ? ' is-quality' : ''}`}
                     style={{ height: `${Math.max(12, Math.round(b.relPlanned * 72))}px` }}>
                  {b.done > 0 && (
                    <div className="hg-fill" style={{ height: `${Math.min(100, Math.round((b.done / b.planned) * 100))}%` }} />
                  )}
                </div>
              </>
            ) : b.done > 0 ? (
              <div className="hg-fill is-solo" style={{ height: `${Math.max(8, Math.round(b.relDone * 72))}px` }} />
            ) : (
              <div className="hg-base" />
            )}
          </div>
        ))}
      </div>
      <div className="hg-letters" aria-hidden="true">
        {view.bars.map((b) => <span key={b.iso} className={b.isToday ? 'is-today' : ''}>{b.letter}</span>)}
      </div>
      {lastWeek && (
        <>
          <hr className="hg-line" />
          <div className="hg-foot" data-testid="hg-lastweek">
            <span>השבוע שעבר</span>
            <span><b dir="ltr">{lastWeek.km}</b> ק״מ · <b dir="ltr">{lastWeek.count}</b> פעילויות</span>
          </div>
        </>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="hg-stack" role="status" aria-label="טוען נתונים" data-testid="hg-loading">
      <div className="hg-skel" style={{ height: 300 }} />
      <div className="hg-pair"><div className="hg-skel" style={{ height: 76 }} /><div className="hg-skel" style={{ height: 76 }} /></div>
      <div className="hg-skel" style={{ height: 200 }} />
    </div>
  );
}

export default function HomeGraphite({
  today = new Date(), plans, activities, hasFeed = false, garmin = null, splits = null, trend = null,
  nextRace = null, loading = false, onNavigate, onConnect, footer = null,
  onDeepAnalyze = null, deepAnalyzing = false, onViewDeepAnalysis = null,
  injury = null, onUpdateInjury = null,
}) {
  const go = (page) => onNavigate && onNavigate(page);
  const todayIso = isoOf(today);
  const days = weekDays(today);
  const planByIso = planDaysByDate(plans);
  const noPlan = !loading && !hasRunningPlan(plans);
  const paused = planPaused(injury);
  const last = hasFeed ? lastRun(activities) : null;
  const lastIso = last ? String(last.date || last.startTimeLocal || '').slice(0, 10) : null;
  const tw = trend && trend.today_workout;
  const coachText = last && trend && trend.insight_text && tw && String(tw.date || '').slice(0, 10) === lastIso
    ? trend.insight_text : null;                              // the stored analysis of THIS run only
  // The deep analysis is offered only for the run on the card: the backend's deep-analysis workout
  // (trend.deep_analysis_ref — the one POST /chat/analyze-workout analyses) has this run's date.
  const deepIso = String((trend && trend.deep_analysis_ref && trend.deep_analysis_ref.date) || '').slice(0, 10);
  const deep = coachText && onDeepAnalyze && deepIso === lastIso
    ? deepAnalysisButton(trend, deepAnalyzing, onDeepAnalyze, onViewDeepAnalysis) : null;
  const todayCard = todayView(planByIso[todayIso], hasFeed ? runsOn(activities, todayIso) : []);
  const next = nextView(planByIso, todayIso);
  const week = weekView(days, planByIso, activities, todayIso, hasFeed);
  const lastWeek = hasFeed ? lastWeekView(days, activities) : null;
  const st = garmin && garmin.syncing ? 'syncing' : garminHomeState(garmin);

  return (
    <div className="hg-screen" data-theme="graphite" dir="rtl" data-testid="home-graphite">
    <div className="hg-page">
      <header className="hg-header">
        <h1 className="hg-title"><img src="/icons/ritmoiq-r.png" alt="" aria-hidden="true" className="hg-logo" />בית</h1>
        <span className="hg-date">{shortDate(today)}</span>
      </header>

      <div className="hg-week" role="group" aria-label="ימי השבוע">
        {days.map((d) => {
          const kind = dotKind(planByIso[d.iso]);
          const isToday = d.iso === todayIso;
          return (
            <button key={d.iso} type="button" data-testid={`hg-day-${d.iso}`} onClick={() => go('weekly-plan')}
                    className={`hg-btn hg-day${isToday ? ' is-today' : ''}${d.iso < todayIso ? ' is-past' : ''}`}
                    aria-label={`${d.name} ${d.dayNum}${isToday ? ', היום' : ''}${kind ? ', אימון מתוכנן' : ''}`}>
              <span className="hg-letter">{d.letter}</span>
              <span className="hg-num hg-dnum" dir="ltr">{d.dayNum}</span>
              <span className={`hg-dot is-${kind || 'none'}`} aria-hidden="true" />
              <span className="hg-under" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {loading || st === 'syncing' ? <Loading /> : (
        <div className="hg-stack">
          {(st === 'disconnected' || st === 'attention') && (
            <div className="hg-alert" role="status" data-testid="hg-device">
              <div>
                <div className="hg-at">{st === 'attention' ? 'Garmin דורש חיבור מחדש' : 'השעון לא מחובר'}</div>
                <div className="hg-as">הביצוע והמדדים יופיעו כאן אחרי חיבור Garmin</div>
              </div>
              <button type="button" className="hg-action" onClick={onConnect}>
                {st === 'attention' ? 'חיבור מחדש' : 'חיבור Garmin'}
              </button>
            </div>
          )}

          {last && (
            <LastWorkout act={last} planDay={planByIso[lastIso]} splits={splits} coachText={coachText}
                         onOpen={() => go('activities')} onCoach={() => go('chat')} deep={deep} />
          )}

          {paused ? (
            <PlanPaused onUpdateInjury={onUpdateInjury} />
          ) : noPlan ? (
            <div className="hg-card" data-testid="hg-noplan">
              <div className="hg-cta-t">עדיין אין תכנית אימונים</div>
              <p className="hg-cta-s">התכנית שלך תופיע כאן ברגע שתיבנה.</p>
              <button type="button" className="hg-action" onClick={() => go('weekly-plan')}>לתכנית האימונים</button>
            </div>
          ) : (todayCard || next) && (
            <div className={`hg-pair${todayCard && next ? '' : ' is-single'}`} data-testid="hg-pair">
              {todayCard && (                                  /* one card alone spans the row — no empty cell */
                <button type="button" className="hg-btn hg-small" onClick={() => go('weekly-plan')}
                        data-testid="hg-today" data-kind={todayCard.kind}>
                  <div className="hg-l">היום</div>
                  <div className="hg-t">{todayCard.title}</div>
                  {todayCard.sub && <div className="hg-s">{todayCard.sub}</div>}
                </button>
              )}
              {next && (
                <button type="button" className="hg-btn hg-small hg-next" onClick={() => go('weekly-plan')}
                        data-testid="hg-next">
                  <div className="hg-l">{next.label}</div>
                  <div className="hg-t">{next.title}</div>
                </button>
              )}
            </div>
          )}

          {!paused && injuryCantRun(injury) && onUpdateInjury && <InjuryUpdateRow onUpdateInjury={onUpdateInjury} />}

          {week.plannedKm !== null && <WeekCard view={week} range={weekRangeLabel(days)} lastWeek={lastWeek} />}

          <button type="button" className="hg-btn hg-row" onClick={() => go('races')} data-testid="hg-races">
            <span className="hg-rt">התחרויות שלי</span>
            <span className="hg-rs">
              {nextRace && nextRace.name
                ? <>{nextRace.name}{daysLabel(nextRace.days) ? ` · ${daysLabel(nextRace.days)}` : ''}</>
                : 'הוספת תחרות'}
              <span aria-hidden="true">←</span>
            </span>
          </button>
          {footer}
        </div>
      )}
    </div>
    </div>
  );
}
